import { X } from "lucide-react";
import { type Transition, motion, useReducedMotion } from "motion/react";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import { NOTIFICATION_ACTION_PRESENTATION } from "./utils";
import { NOTIFICATION_ACTIONS, NOTIFICATION_BULK_MAX_IDS, type NotificationAction } from "../constants";
import type { useNotificationSelection } from "../hooks/useNotificationSelection";

import { Button } from "@/components/ui/v2/button";
import { Checkbox } from "@/components/ui/v2/checkbox";

const SCOPE_ACTION_CLASS =
  "font-medium text-primary underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none";

const fadeTransition: Transition = { duration: 0.15, ease: [0.25, 1, 0.5, 1] };

type NotificationSelectionBarProps = {
  selection: ReturnType<typeof useNotificationSelection>;
  totalRecords: number;
  readAction: NotificationAction | undefined;
  isReadActionLoading: boolean;
  archiveAction: NotificationAction;
  isPending: boolean;
  onAction: (action: NotificationAction) => void;
};

type BulkActionButtonProps = {
  action: NotificationAction;
  disabled: boolean;
  describedBy: string | undefined;
  isBusy?: boolean;
  onAction: (action: NotificationAction) => void;
};

function BulkActionButton({ action, disabled, describedBy, isBusy, onAction }: BulkActionButtonProps) {
  const { t } = useTranslation();
  const { labelKey, icon: Icon } = NOTIFICATION_ACTION_PRESENTATION[action];
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="pointer-coarse:h-11"
      onClick={() => onAction(action)}
      disabled={disabled}
      aria-busy={isBusy || undefined}
      aria-describedby={describedBy}
    >
      <Icon aria-hidden="true" />
      <span className="max-sm:sr-only">{t(labelKey)}</span>
    </Button>
  );
}

type SelectionScopeProps = Pick<NotificationSelectionBarProps, "selection" | "totalRecords">;

function SelectionScope({ selection, totalRecords }: SelectionScopeProps) {
  const { t } = useTranslation();

  if (selection.mode === "matching") {
    return (
      <p className="bg-muted px-4 py-2 text-center text-sm">
        {t("notifications.selection.allMatchingSelected", { count: selection.selectedCount })}{" "}
        <button type="button" onClick={selection.clear} className={SCOPE_ACTION_CLASS}>
          {t("notifications.selection.clear")}
        </button>
      </p>
    );
  }

  if (!selection.canSelectAllMatching) return null;

  return (
    <p className="bg-muted px-4 py-2 text-center text-sm">
      {t("notifications.selection.allLoadedSelected", { count: selection.loadedCount })}{" "}
      <button type="button" onClick={selection.selectAllMatching} className={SCOPE_ACTION_CLASS}>
        {t("notifications.selection.selectAllMatching", { count: totalRecords })}
      </button>
    </p>
  );
}

export function NotificationSelectionBar({
  selection,
  totalRecords,
  readAction,
  isReadActionLoading,
  archiveAction,
  isPending,
  onAction,
}: NotificationSelectionBarProps) {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();
  const limitHintId = useId();
  const hasSelection = selection.mode !== "none";
  const isActionBlocked = isPending || selection.exceedsIdLimit;
  const limitHintDescribedBy = selection.exceedsIdLimit ? limitHintId : undefined;

  return (
    <div className="border-b">
      <div className="flex min-h-12 items-center gap-3 px-4 py-2">
        <Checkbox
          checked={selection.headerState}
          onCheckedChange={selection.toggleAllLoaded}
          disabled={selection.loadedCount === 0}
          aria-label={t("notifications.selection.selectAll")}
          className="pointer-coarse:size-5"
        />
        <span className="sr-only" aria-live="polite">
          {hasSelection ? t("notifications.selection.selected", { count: selection.selectedCount }) : ""}
        </span>
        {hasSelection ? (
          <motion.div
            className="flex min-w-0 flex-1 items-center gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={prefersReducedMotion ? { duration: 0 } : fadeTransition}
          >
            <span aria-hidden="true" className="text-sm font-medium tabular-nums">
              {t("notifications.selection.selected", { count: selection.selectedCount })}
            </span>
            <div className="ml-auto flex items-center gap-1">
              {(readAction || isReadActionLoading) && (
                <BulkActionButton
                  action={readAction ?? NOTIFICATION_ACTIONS.READ}
                  disabled={isActionBlocked || isReadActionLoading}
                  isBusy={isReadActionLoading}
                  describedBy={limitHintDescribedBy}
                  onAction={onAction}
                />
              )}
              <BulkActionButton
                action={archiveAction}
                disabled={isActionBlocked}
                describedBy={limitHintDescribedBy}
                onAction={onAction}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="pointer-coarse:size-11"
                onClick={selection.clear}
                aria-label={t("notifications.selection.clear")}
              >
                <X aria-hidden="true" />
              </Button>
            </div>
          </motion.div>
        ) : (
          <span className="text-sm text-muted-foreground tabular-nums">
            {t("notifications.selection.total", { count: totalRecords })}
          </span>
        )}
      </div>
      <SelectionScope selection={selection} totalRecords={totalRecords} />
      {selection.exceedsIdLimit && (
        <p id={limitHintId} className="px-4 pb-2 text-xs text-muted-foreground">
          {t("notifications.selection.limitHint", { max: NOTIFICATION_BULK_MAX_IDS })}
        </p>
      )}
    </div>
  );
}
